import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { useEffect, useState } from "react";
import toast from 'react-hot-toast';
interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
}


export default function Pagination({
  currentPage,
  totalPages,
  totalCount,
  itemsPerPage,
  onPageChange,
}: PaginationProps) {
    const [pageInput, setPageInput] = useState(String(currentPage));

  useEffect(() => {
    setPageInput(String(currentPage));
  }, [currentPage]);
  if (totalPages <= 1) return null;

  const from = (currentPage - 1) * itemsPerPage + 1;
  const to = Math.min(currentPage * itemsPerPage, totalCount);

  const getPageNumbers = (delta = 2): number[] => {
    const pages: number[] = [];
    for (
      let i = Math.max(1, currentPage - delta);
      i <= Math.min(totalPages, currentPage + delta);
      i++
    ) {
      pages.push(i);
    }
    return pages;
  };

  const handleGo = () => {
    const page = Number(pageInput);

    if (isNaN(page)) return;

    if (page < 1) {
      setPageInput("1");
      onPageChange(1);
      return;
    }

    if (page > totalPages) {
      toast.error(`Maximum page is ${totalPages}`);

      setPageInput(String(totalPages));

      onPageChange(totalPages);

      return;
    }

    onPageChange(page);
  };

  const navBtn =
    'p-2 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-brand-50 hover:border-brand-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed';

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-1 py-4 border-t border-gray-100">
      {/* Count info */}
      <p className="text-sm text-gray-500 order-2 sm:order-1">
  Showing{' '}
  <span className="font-semibold text-gray-800">
    {from}–{to}
  </span>
  {' '}of{' '}
  <span className="font-semibold text-gray-800">
    {totalCount}
  </span>
  {' '}items
</p>

      {/* Controls */}
      <div className="flex items-center gap-1.5 order-1 sm:order-2">
        {/* First */}
        <button
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
          className={navBtn}
          aria-label="First page"
        >
          <ChevronsLeft className="w-4 h-4" />
        </button>

        {/* Prev */}
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={navBtn}
          aria-label="Previous page"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>

        {/* Page numbers — desktop */}
        <div className="hidden sm:flex items-center gap-1">
          {getPageNumbers(2).map((page) => (
            <button
              key={page}
              onClick={() => onPageChange(page)}
              className={`min-w-[36px] h-9 px-2 rounded-lg border text-sm font-semibold transition-colors ${page === currentPage
                  ? 'bg-brand-500 border-brand-500 text-white'
                  : 'bg-white border-gray-200 text-gray-600 hover:bg-brand-50 hover:border-brand-300'
                }`}
              aria-current={page === currentPage ? 'page' : undefined}
            >
              {page}
            </button>
          ))}
        </div>
                <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={navBtn}
          aria-label="Next page"
        >
          <ChevronRight className="w-4 h-4" />
        </button>

        {/* Last */}
        <button
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages}
          className={navBtn}
          aria-label="Last page"
        >
          <ChevronsRight className="w-4 h-4" />
        </button>

        {/* Page info — mobile */}
        <div className="flex items-center gap-2">
          <input
            type="text"
            min={1}
            max={totalPages}
            value={pageInput}
            onChange={(e) => {
              const value = e.target.value;

              if (value === "") {
                setPageInput("");
                return;
              }

              const num = Number(value);

              if (num <= totalPages) {
                setPageInput(value);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleGo();
              }
            }}
            className="w-16 h-9 border rounded-lg px-2 text-center"
          />

          <button
            onClick={handleGo}
            className="h-9 px-3 rounded-lg bg-brand-500 text-white hover:bg-brand-600"
          >
            Go
          </button>
        </div>
      </div>
    </div>
  );
}
